/**
 * EffectiveTimePrint — impression du rapport Time « Effective time ».
 * Page 1 : Consolidation (heures saisies par personne, 5 catégories + total).
 * Page 2 : Recap (saisi vs théorique pro-rata).
 * Mêmes sources et formules que EffectiveTimePage (onglet Consolidation + Recap).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listEffectiveTime, getEffectiveTimeRecap } from '../../api/tdabc/effective_time';
import api from '../../api/client';
import PrintReportHeader from '../hr/print/PrintReportHeader';
import '../hr/print/PeoplePrint.css';

const CATEGORY_FIELDS = ['billable_hours', 'nonbillable_hours', 'corp_mgmt_hours', 'pool_mgmt_hours', 'absence_hours'];
const CATEGORY_LABELS = ['Billable', 'NB Prod', 'Corp Mgmt', 'Pool Mgmt', 'Absence'];
const MONTH_NUMS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const FMT = new Intl.NumberFormat('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
const fmtH = (v) => FMT.format(Number(v) || 0);
const fmtDelta = (v) => (v == null ? '—' : (Number(v) >= 0 ? '+' : '') + FMT.format(Number(v)));

export default function EffectiveTimePrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t } = useTranslation();
  const tenantId = activeTenant?.id;
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!tenantId) return undefined;
    Promise.all([
      listEffectiveTime(tenantId, year).then(r => r.data || r || []),
      api.get(`/tdabc/people/${tenantId}`).then(r => r.data?.data || r.data || []),
      api.get(`/tdabc/cost-pools/${tenantId}`).then(r => r.data?.data || r.data || []),
      getEffectiveTimeRecap(tenantId, year).then(r => r.data || []).catch(() => []),
    ]).then(([entries, people, pools, recap]) => {
      if (!alive) return;
      const poolMap = {};
      (pools || []).forEach(p => { poolMap[p.id] = p; });
      setData({ entries: entries || [], people: people || [], poolMap, recap: recap || [] });
      if (onDataReady) onDataReady('effective-time');
    }).catch(() => { if (onDataReady) onDataReady('effective-time'); });
    return () => { alive = false; };
  }, [tenantId, year]); // eslint-disable-line

  const consolidation = useMemo(() => {
    if (!data) return { rows: [], totals: [], grand: 0 };
    // lookup[person_id][month] = entry
    const lookup = {};
    for (const e of data.entries) {
      (lookup[e.person_id] || (lookup[e.person_id] = {}))[e.month] = e;
    }
    const sumField = (pid, f) => MONTH_NUMS.reduce((s, m) => s + (Number(lookup[pid]?.[m]?.[f]) || 0), 0);
    const people = [...data.people].sort((a, b) => (a.person_name || '').localeCompare(b.person_name || '', 'fr'));
    const rows = people.map(p => {
      const cats = CATEGORY_FIELDS.map(f => sumField(p.id, f));
      return {
        id: p.id,
        person_name: p.person_name,
        pool_name: data.poolMap[p.cost_pool_id]?.name || '—',
        cats,
        total: cats.reduce((s, v) => s + v, 0),
      };
    });
    const totals = CATEGORY_FIELDS.map((_, i) => rows.reduce((s, r) => s + r.cats[i], 0));
    const grand = totals.reduce((s, v) => s + v, 0);
    return { rows, totals, grand };
  }, [data]);

  if (!data) return <div className="ppl-print__section"><p>Chargement…</p></div>;

  const { rows, totals, grand } = consolidation;
  const recap = data.recap;
  const recapTotal = recap.reduce((a, r) => ({
    saisi: a.saisi + (r.saisi || 0),
    theorique: a.theorique + (r.theorique || 0),
    delta: a.delta + (r.delta || 0),
  }), { saisi: 0, theorique: 0, delta: 0 });

  return (
    <>
      {/* ── Page 1 : Consolidation ── */}
      <section className="ppl-print__section ppl-print__section--wide">
        <PrintReportHeader category="Time" title="Effective time — Consolidation" year={year} tenant={activeTenant} editedAt={editedAt} />
        <table className="ppl-print__table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Person</th>
              <th style={{ textAlign: 'left' }}>Pool</th>
              {CATEGORY_LABELS.map(l => <th key={l}>{l}</th>)}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td style={{ textAlign: 'left' }}>{r.person_name}</td>
                <td style={{ textAlign: 'left' }}>{r.pool_name}</td>
                {r.cats.map((v, i) => <td key={i}>{v ? fmtH(v) : '—'}</td>)}
                <td>{r.total ? fmtH(r.total) : '—'}</td>
              </tr>
            ))}
            <tr>
              <td style={{ textAlign: 'left' }} colSpan={2}><strong>Total ({rows.length})</strong></td>
              {totals.map((v, i) => <td key={i}><strong>{fmtH(v)}</strong></td>)}
              <td><strong>{fmtH(grand)}</strong></td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* ── Page 2 : Recap (saisi vs théorique) ── */}
      {recap.length > 0 && (
        <section className="ppl-print__section">
          <PrintReportHeader category="Time" title={t('phrases:p_e8f4c4db60')} year={year} tenant={activeTenant} editedAt={editedAt} />
          <table className="ppl-print__table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Personne</th>
                <th style={{ textAlign: 'left' }}>Pool</th>
                <th>Mois bill.</th>
                <th>Saisi (h)</th>
                <th>Théo. pro-rata (h)</th>
                <th>Delta</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recap.map(r => {
                const ok = Math.abs(r.delta) <= 2;
                return (
                  <tr key={r.person_id}>
                    <td style={{ textAlign: 'left' }}>{r.person_name}</td>
                    <td style={{ textAlign: 'left' }}>{r.cost_pool_name || '—'}</td>
                    <td>{r.months_realized ?? 0}/12</td>
                    <td>{fmtH(r.saisi)}</td>
                    <td>{fmtH(r.theorique)}</td>
                    <td style={{ color: r.delta >= 0 ? '#3B6D11' : '#A32D2D' }}>{fmtDelta(r.delta)}</td>
                    <td style={{ color: ok ? '#3B6D11' : '#BA7517', fontWeight: 600 }}>{ok ? '✓' : '⚠'}</td>
                  </tr>
                );
              })}
              <tr>
                <td style={{ textAlign: 'left' }} colSpan={3}><strong>Total ({recap.length} pers.)</strong></td>
                <td><strong>{fmtH(recapTotal.saisi)}</strong></td>
                <td><strong>{fmtH(recapTotal.theorique)}</strong></td>
                <td><strong>{fmtDelta(recapTotal.delta)}</strong></td>
                <td />
              </tr>
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
